/**
 * HTML Security Utilities
 *
 * Functions for sanitizing HTML content to prevent XSS attacks.
 * Used by rich text renderers and any component that generates HTML from user data.
 *
 * Defense in depth strategy:
 * 1. Escape text content during HTML generation (escapeHtml)
 * 2. Sanitize URLs to block dangerous protocols (sanitizeUrl)
 * 3. Escape attribute values (escapeAttribute)
 * 4. Final DOMPurify pass on rendered HTML (sanitizeHtml)
 */

import DOMPurify from "isomorphic-dompurify";
import { logger } from "~/lib/logger/logger";
import { DOMPURIFY_CONFIG } from "./htmlSanitizerConfig";

/**
 * Sanitizes HTML content using DOMPurify
 *
 * This is the final defense layer that removes any XSS vectors
 * that might have slipped through earlier sanitization.
 *
 * Uses isomorphic-dompurify which works in both browser and Node.js (SSR).
 *
 * @param html - HTML string to sanitize
 * @returns Sanitized HTML string safe for innerHTML
 */
export function sanitizeHtml(html: string): string {
  if (!html || typeof html !== "string") return "";

  return DOMPurify.sanitize(html, DOMPURIFY_CONFIG);
}

/**
 * Escapes HTML special characters to prevent XSS
 *
 * Converts: & < > " ' to their HTML entity equivalents
 *
 * @param text - Raw text that may contain HTML special characters
 * @returns Text with HTML entities escaped
 */
export function escapeHtml(text: string): string {
  const htmlEscapes: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };
  return text.replace(/[&<>"']/g, (char) => htmlEscapes[char] || char);
}

/**
 * Validates and sanitizes URLs to prevent javascript: and data: XSS attacks
 *
 * Only allows safe protocols: http, https, mailto, tel
 * Also allows relative URLs (/, #, //) and protocol-relative URLs
 *
 * @param url - URL string to validate
 * @returns Sanitized URL or "#" if the URL is invalid/dangerous
 */
export function sanitizeUrl(url: string): string {
  if (!url || typeof url !== "string") return "#";

  const trimmedUrl = url.trim();

  // Block protocol-relative URLs (//evil.com) — these inherit the page protocol
  // and can be used for phishing. CMS content should use explicit https:// links.
  if (trimmedUrl.startsWith("//")) {
    logger.warn("[htmlSecurity] Blocked protocol-relative URL:", url);
    return "#";
  }

  // Allow relative URLs starting with /
  if (trimmedUrl.startsWith("/")) return trimmedUrl;

  // Allow anchor links
  if (trimmedUrl.startsWith("#")) return trimmedUrl;

  // Check for safe protocols
  const safeProtocols = ["http://", "https://", "mailto:", "tel:"];
  const lowerUrl = trimmedUrl.toLowerCase();

  for (const protocol of safeProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      return trimmedUrl;
    }
  }

  // Block dangerous protocols (javascript:, data:, vbscript:, etc.)
  if (lowerUrl.includes(":")) {
    logger.warn("[htmlSecurity] Blocked potentially dangerous URL:", url);
    return "#";
  }

  // Default: treat as relative URL
  return trimmedUrl;
}

/**
 * Escapes a string for safe use in HTML attributes
 *
 * Converts " and ' to their HTML entity equivalents
 *
 * @param value - String to escape for use in an HTML attribute
 * @returns Escaped string safe for attribute values
 */
export function escapeAttribute(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
